import axios from "axios";
import { API, graphqlOperation } from "aws-amplify";
import { getGuest, listGuests } from "../graphql/queries";

const REST_API_URL = process.env.REACT_APP_REST_API_URL;
const API_KEY = process.env.REACT_APP_LOOKUP_API_KEY;

const calculateMatchScore = (user, searchCriteria) => {
  let score = 0;

  // Example: Full name match
  if (
    user.name.toLowerCase() ===
    `${searchCriteria.firstName.toLowerCase()} ${searchCriteria.lastName.toLowerCase()}`
  ) {
    score += 50;
  } else {
    // Partial name match
    if (
      user.name.toLowerCase().includes(searchCriteria.firstName.toLowerCase())
    ) {
      score += 20;
    }
    if (
      user.name.toLowerCase().includes(searchCriteria.lastName.toLowerCase())
    ) {
      score += 20;
    }
  }

  // Granular email match based on matching characters
  if (user.email && searchCriteria.email) {
    const userEmailLower = user.email.toLowerCase();
    const searchEmailLower = searchCriteria.email.toLowerCase();

    // Exact match
    if (userEmailLower === searchEmailLower) {
      score += 50;
    } else {
      // Calculate the number of matching characters
      let matchingChars = 0;
      for (let i = 0; i < searchEmailLower.length; i++) {
        if (userEmailLower[i] === searchEmailLower[i]) {
          matchingChars++;
        } else {
          break; // Stop counting when characters no longer match sequentially
        }
      }

      // Score based on the percentage of matching characters
      const matchPercentage = (matchingChars / searchEmailLower.length) * 100;
      score += matchPercentage * 0.5; // Adjust this multiplier as needed
    }
  }

  // Add more criteria as needed, adjusting the score

  return score;
};

export const findBestMatch = async (searchCriteria) => {
  try {
    let nextToken = null;
    let allUsers = [];

    // 1. Search by reservationID via REST API
    if (
      (searchCriteria.bID || searchCriteria.reservationID) &&
      searchCriteria.ownerID
    ) {
      try {
        const payload = {
          ownerID: searchCriteria.ownerID, // OwnerID is always required
        };

        // Include either bID or reservationID (whichever exists)
        if (searchCriteria.bID) {
          payload.bID = searchCriteria.bID;
        } else if (searchCriteria.reservationID) {
          payload.reservationID = searchCriteria.reservationID;
        }

        const response = await axios.post(REST_API_URL, payload, {
          headers: {
            "Content-Type": "application/json",
            "x-api-key": API_KEY, // Add API key header
          },
        });

        if (response.data && response.data.success && response.data.data) {
          const user = response.data.data;

          // Return a clean user object
          return {
            id: user.id,
            name: user.name,
            notes: null,
            phone: user.phone,
            email: user.email,
            Bookings: user.Bookings || { items: [], nextToken: null },
            createdAt: user.createdAt,
            updatedAt: user.updatedAt,
            owner: user.owner,
            isSecure: true,
            score: 100,
          };
        }
      } catch (error) {
        console.error("Error fetching data via REST API:", error.message);
      }
    }
    // if (searchCriteria.reservationID) {
    //   try {
    //     let allReservationUsers = [];

    //     do {
    //       const reservationData = await API.graphql({
    //         query: `
    //           query ListGuestsByReservationID($nextToken: String) {
    //             listGuests(nextToken: $nextToken) {
    //               items {
    //                 id
    //                 name
    //                 notes
    //                 phone
    //                 email
    //                 createdAt
    //                 updatedAt
    //                 owner
    //                 Bookings(filter: {reservationID: {eq: "${searchCriteria.reservationID}"}}) {
    //                   items {
    //                     id
    //                     reservationID
    //                   }
    //                 }
    //               }
    //               nextToken
    //             }
    //           }
    //         `,
    //         variables: { nextToken: nextToken },
    //       });

    //       const reservationUsers = reservationData.data.listGuests.items.filter(
    //         (guest) =>
    //           guest.Bookings.items.some(
    //             (booking) =>
    //               booking.reservationID === searchCriteria.reservationID
    //           ) // Ensure reservationID matches
    //       );

    //       allReservationUsers = allReservationUsers.concat(reservationUsers);

    //       nextToken = reservationData.data.listGuests.nextToken;
    //     } while (nextToken);

    //     // allUsers = allUsers.concat(allReservationUsers);

    //     if (allReservationUsers.length > 0) {
    //       // console.log(allReservationUsers);
    //       reservationExists = true;
    //       allUsers = allUsers.concat(allReservationUsers);
    //     }
    //   } catch (error) {
    //     console.error("Error searching for users by reservation ID:", error);
    //   }
    // }

    console.log(searchCriteria);

    console.log(allUsers);

    // 2. Fallback: Search by name or email using GraphQL
    do {
      const filter = {
        or: [],
      };

      // Add filters if the corresponding search criteria exist
      if (searchCriteria.firstName) {
        filter.or.push({ name: { contains: searchCriteria.firstName } });
      }
      if (searchCriteria.lastName) {
        filter.or.push({ name: { contains: searchCriteria.lastName } });
      }
      if (searchCriteria.email) {
        filter.or.push({ email: { contains: searchCriteria.email } });
      }

      // Proceed with the query if there's at least one filter
      if (filter.or.length > 0) {
        const userData = await API.graphql(
          graphqlOperation(listGuests, { filter, limit: 1000, nextToken })
        );

        if (userData.data && userData.data.listGuests.items.length > 0) {
          allUsers = allUsers.concat(userData.data.listGuests.items);
        }

        nextToken = userData.data.listGuests.nextToken;
      }
    } while (nextToken);

    // 3. Fetch details, ensure checkInDate matches, and calculate scores
    if (allUsers.length > 0) {
      const detailedUsers = await Promise.all(
        allUsers.map(async (user) => {
          try {
            const userDetailData = await API.graphql(
              graphqlOperation(getGuest, { id: user.id })
            );

            let userDetails = userDetailData.data.getGuest;

            const hasMatchingCheckInDate = userDetails.Bookings.items.some(
              (booking) => booking.checkInDate === searchCriteria.checkInDate
            );

            return {
              ...userDetails,
              isSecure: hasMatchingCheckInDate,
            };
          } catch (error) {
            console.error(`Error fetching details for user ${user.id}:`, error);
            return null;
          }
        })
      );

      const validDetailedUsers = detailedUsers.filter((user) => user !== null);

      const scoredUsers = validDetailedUsers.map((user) => ({
        ...user,
        score: calculateMatchScore(user, searchCriteria),
      }));

      // Sort users by score, descending
      scoredUsers.sort((a, b) => b.score - a.score);

      // Get the highest score
      const highestScore = scoredUsers[0].score;

      // Filter out users with the highest score
      const topScoredUsers = scoredUsers.filter(
        (user) => user.score === highestScore
      );

      // Sort the top scored users by creation date to return the most recently added users
      topScoredUsers.sort(
        (a, b) => new Date(b.createdAt) - new Date(a.createdAt)
      );

      // Return the user with the highest score
      return topScoredUsers[0];
    } else {
      console.error("No users found matching the criteria.");
      return null;
    }
  } catch (error) {
    console.error("Error searching for user by criteria:", error);
    return null;
  }
};
